import { useEffect, useMemo } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import { Button } from 'govuk-frontend';
import { RequireAuth } from '../../auth/RequireAuth';
import PsfaGuide from '../../config/pages/psfa/guide/PsfaGuide';
import config from '../../config';
import { FormConfigProvider } from '../../context/FormConfigContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import EnterAppMFACodeScreen from '../../screens/AppMFA/EnterAppMFACodeScreen';
import Declaration from '../../screens/DeclarationScreen/DeclarationScreen';
import DeclarationSuccessScreen from '../../screens/DeclarationSuccessScreen/DeclarationSuccessScreen';
import CookiesScreen from '../../screens/CookiesScreen/CookiesScreen';
import AccessibilityStatementScreen from '../../screens/AccessibilityStatementScreen/AccessibilityStatementScreen';
import DefaultErrorScreen from '../../screens/ErrorScreen/DefaultErrorScreen';
import Error404Screen from '../../screens/ErrorScreen/Error404Screen';
import GlobalErrorScreen from '../../screens/ErrorScreen/GlobalErrorScreen';
import { FormSummary } from '../../screens/FormSummary/FormSummary';
import Overview from '../../screens/OverviewScreen/OverviewScreen';
import ScsModeration from '../../screens/ScsModeration/ScsModeration';
import PrivacyNoticeScreen from '../../screens/PrivacyNoticeScreen/PrivacyNoticeScreen';
import ResetPasswordEmailScreen from '../../screens/ResetPassword/ResetPasswordEmailScreen';
import ResetPasswordScreen from '../../screens/ResetPassword/ResetPasswordScreen';
import RpaLoginScreen from '../../screens/RpaLoginScreen/RpaLoginScreen';
import { isRpaLoginTenant } from '../../screens/RpaLoginScreen/rpaLoginConfig';
import SetNewPasswordScreen from '../../screens/SetNewPassword/SetNewPasswordScreen';
import ShareScreen from '../../screens/ShareScreen/ShareScreen';
import SigninScreen from '../../screens/SigninScreen/SigninScreen';
import SubsectionScreen from '../../screens/SubsectionScreen/SubsectionScreen';
import SummaryScreen from '../../screens/SummaryScreen/SummaryScreen';
import TermsOfUseScreen from '../../screens/TermsOfUseScreen/TermsOfUseScreen';
import AccountPage from '../AccountPage/AccountPage';
import type { SubsectionRouterProps } from './SubsectionRouterProps';
import { isValidTenant } from '../../utils/tenant-utils';
import { useParams } from 'react-router-dom';

const { urls } = config;

const Account = () => (
  <AccountPage>
    <Outlet />
  </AccountPage>
);

const AuthenticatedTenantContent = () => {
  const { tenant } = useParams<{ tenant: string }>();

  if (isRpaLoginTenant(tenant)) {
    return <RpaLoginScreen />;
  }

  return <Account />;
};

const TenantRoutes = () => {
  const { formConfig, isLoading, error } = useFormConfig();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return <Outlet />;
};

const PsfaGuideRoute = () => {
  const { tenant } = useParams();
  return tenant === 'psfa' ? <RequireAuth><PsfaGuide /></RequireAuth> : <Error404Screen />;
};

const ValidTenantRoute = () => {
  const { tenant } = useParams<{ tenant: string }>();
  if (!isValidTenant(tenant)) {
    return <GlobalErrorScreen messageOverride="Error: The form you requested cannot be found" />;
  }

  return (
    <FormConfigProvider>
      <TenantRoutes />
    </FormConfigProvider>
  );
};

const SubsectionRoutes = () => {
  const { formConfig } = useFormConfig();
  const showSections = formConfig.showSections === undefined ? true : formConfig.showSections;

  const subsectionProps: SubsectionRouterProps[] = useMemo(
    () =>
      formConfig.sections?.reduce(
        (accumulator: SubsectionRouterProps[], section, sectionIndex) => [
          ...accumulator,
          ...(section?.subsections || []).map((subsection, subsectionIndex) => ({
            subsection,
            urlPath: `${section.slug}/${subsection.slug}`,
            sectionTitle: subsection.title,
            sectionName: section.title,
            sectionIndex: sectionIndex,
            subsectionIndex,
          })),
        ],
        [],
      ),
    [formConfig],
  );

  return (
    <Routes>
      {subsectionProps?.map((props, index) => (
        <Route path={props.urlPath} key={props.urlPath}>
          <Route path="summary" element={<SummaryScreen {...props} />} />
          <Route
            path=":page?"
            element={
              <SubsectionScreen
                {...props}
                sectionTitle={
                  (() => {
                    const [sectionSlug] = props.urlPath.split('/');
                    const sectionIdx =
                      formConfig.sections?.findIndex((s) => s.slug === sectionSlug) ?? -1;
                    const subsectionIdx =
                      sectionIdx >= 0
                        ? formConfig.sections?.[sectionIdx]?.subsections.findIndex(
                            (s) => s.slug === props.subsection.slug,
                          ) ?? -1
                        : -1;
                    const sectionNumber = sectionIdx >= 0 ? sectionIdx + 1 : undefined;
                    const subsectionNumber = subsectionIdx >= 0 ? subsectionIdx + 1 : undefined;
                    return showSections
                      ? `${sectionNumber ?? ''}.${subsectionNumber ?? ''} ${props.sectionTitle}`
                      : `${subsectionNumber ?? ''}. ${props.sectionTitle}`;
                  })()
                }
              />
            }
          />
        </Route>
      ))}
    </Routes>
  );
};

const GOVUK_BUTTON_SELECTOR = '[data-module="govuk-button"]:not([data-govuk-button-init])';

const initGovUkButtonsInScope = (scope: ParentNode) => {
  const initialize = (element: Element) => {
    if (!(element instanceof HTMLElement)) return;
    if (!element.matches(GOVUK_BUTTON_SELECTOR)) return;
    try {
      new Button(element);
    } catch (_error) {
      // No-op: skip buttons that cannot be initialised in this pass.
    }
  };

  if (scope instanceof HTMLElement) {
    initialize(scope);
  }

  scope.querySelectorAll(GOVUK_BUTTON_SELECTOR).forEach((element) => {
    initialize(element);
  });
};

const GovUkFrontendEnhancements = () => {
  useEffect(() => {
    initGovUkButtonsInScope(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            initGovUkButtonsInScope(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
};

function Router() {
  return (
    <>
      <GovUkFrontendEnhancements />
      <Routes>
        {/* Global error route(s) that must not be interpreted as a tenant slug */}
        <Route path="/error/*" element={<GlobalErrorScreen />} />

        {/* Parent route that captures the tenant parameter */}
        <Route
          path="/:tenant"
          element={
            <ValidTenantRoute />
          }
        >
          <Route path="signin" element={<SigninScreen />} />
          <Route path="reset-password-email" element={<ResetPasswordEmailScreen />} />
          <Route path="reset-password" element={<ResetPasswordScreen />} />
          <Route path="enter-app-mfa-code" element={<EnterAppMFACodeScreen />} />
          <Route path="set-new-password" element={<SetNewPasswordScreen onSuccess={() => {}} />} />
          <Route path="cookies" element={<CookiesScreen />} />
          <Route path="accessibility" element={<AccessibilityStatementScreen />} />
          <Route path="privacy" element={<PrivacyNoticeScreen />} />
          <Route path="terms-of-use" element={<TermsOfUseScreen />} />
          <Route path="guide" element={<PsfaGuideRoute />} />
          <Route path="moderation" element={<RequireAuth><ScsModeration /></RequireAuth>} />

          <Route
            element={
              <RequireAuth>
                <AuthenticatedTenantContent />
              </RequireAuth>
            }
          >
            <Route path="" element={<Overview />} />
            <Route path="summary" element={<FormSummary />} />
            <Route path="share" element={<ShareScreen />} />
            <Route path="declaration" element={<Declaration />} />
            <Route path="declaration/success" element={<DeclarationSuccessScreen />} />
            <Route path="*" element={<SubsectionRoutes />} />
          </Route>

          <Route path="error" element={<DefaultErrorScreen />} />
        </Route>

        <Route path="*" element={<Error404Screen />} />
      </Routes>
    </>
  );
}

export default Router;
